import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { VaultPartnerSidebar } from "@/components/vault/VaultPartnerSidebar";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, User } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";

export function VaultPartnerLayout() {
  const [loading, setLoading] = useState(true);
  const [vaultPartnerId, setVaultPartnerId] = useState<string | null>(null);
  const [partnerName, setPartnerName] = useState<string>("");
  const navigate = useNavigate();

  useEffect(() => {
    checkVaultPartnerAccess();
  }, []);

  const checkVaultPartnerAccess = async () => {
    try {
      console.log("🔍 Checking vault partner access...");
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.log("❌ No user found, redirecting to auth");
        navigate("/auth");
        return;
      }

      console.log("✅ User authenticated:", user.id);

      // First check if user is an admin - admins get access to SwapX Vault portal
      const { data: isAdmin } = await supabase.rpc('has_role', { 
        _user_id: user.id, 
        _role: 'admin' 
      });

      if (isAdmin) {
        console.log("✅ Admin user detected, granting SwapX Vault access");
        
        // Get SwapX Vault partner info
        const { data: swapxPartner, error: partnerError } = await supabase
          .from("vault_partners")
          .select("id, name")
          .eq("partner_id", "swapx")
          .single();

        if (partnerError || !swapxPartner) {
          console.error("❌ SwapX Vault not found:", partnerError);
          navigate("/admin");
          return;
        }

        setVaultPartnerId(swapxPartner.id);
        setPartnerName("SwapX Vault (Staff)");
        console.log("✅ Access granted! Partner ID:", swapxPartner.id);
        setLoading(false);
        return;
      }

      // Check if user has vault partner access
      const { data: vaultPartnerUser, error } = await supabase
        .from("vault_partner_users")
        .select("vault_partner_id, vault_role")
        .eq("user_id", user.id)
        .single();

      console.log("vault_partner_users query result:", { vaultPartnerUser, error });

      if (error || !vaultPartnerUser) {
        console.error("❌ No vault partner access:", error);
        navigate("/");
        return;
      }

      console.log("✅ Vault partner user found:", vaultPartnerUser);

      // Get partner name separately
      const { data: partner, error: partnerError } = await supabase
        .from("vault_partners")
        .select("name, contact_email")
        .eq("id", vaultPartnerUser.vault_partner_id)
        .single();

      console.log("vault_partners query result:", { partner, partnerError });

      setVaultPartnerId(vaultPartnerUser.vault_partner_id);
      setPartnerName(partner?.name || partner?.contact_email || "Vault Partner");
      console.log("✅ Access granted! Partner ID:", vaultPartnerUser.vault_partner_id);
    } catch (error) {
      console.error("❌ Error checking vault partner access:", error);
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!vaultPartnerId) {
    return null;
  }

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <VaultPartnerSidebar partnerName={partnerName} />
        <div className="flex-1 flex flex-col">
          <header className="h-14 border-b flex items-center justify-between px-4 sticky top-0 bg-background z-10">
            <SidebarTrigger />
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-primary/10">
                      <User className="h-5 w-5" />
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 bg-popover z-50" align="end">
                <div className="flex items-center justify-start gap-2 p-2">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium">{partnerName}</p>
                    <p className="text-xs text-muted-foreground">SwapX Vault Operations</p>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut}>
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </header>
          <main className="flex-1">
            <Outlet />
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
